import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface UserProfile {
  id: string;
  name: string | null;
  email: string;
  avatar_url: string | null;
  role: string;
  tenant_name: string;
  created_at: string;
}

export const useProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Fetch user profile data
      const { data: publicData } = await supabase
        .from('users_public')
        .select('*')
        .eq('id', user.id)
        .single();

      const { data: privateData } = await supabase
        .from('user_private_details')
        .select('email')
        .eq('user_id', user.id)
        .single();

      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role, tenant_id, tenants(name)')
        .eq('user_id', user.id)
        .single();

      if (!publicData) return null;

      return {
        id: publicData.id,
        name: publicData.name,
        email: privateData?.email || '',
        avatar_url: publicData.avatar_url,
        role: roleData?.role || 'staff',
        tenant_name: (roleData?.tenants as any)?.name || '',
        created_at: publicData.created_at,
      } as UserProfile;
    },
    enabled: !!user?.id,
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (updates: { name?: string; email?: string }) => {
      if (!user?.id) throw new Error('No user ID');

      // Update public profile
      if (updates.name !== undefined) {
        const { error: publicError } = await supabase
          .from('users_public')
          .update({ name: updates.name })
          .eq('id', user.id);

        if (publicError) throw publicError;
      }

      // Update email if changed
      if (updates.email !== undefined && updates.email !== profile?.email) {
        const { error: privateError } = await supabase
          .from('user_private_details')
          .update({ email: updates.email })
          .eq('user_id', user.id);

        if (privateError) throw privateError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile', user?.id] });
      toast.success('Profile updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update profile');
    },
  });

  const uploadAvatar = async (file: File) => {
    if (!user?.id) {
      toast.error('No user ID');
      return;
    }

    try {
      setUploading(true);

      // Upload to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('users_public')
        .update({ avatar_url: urlData.publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      queryClient.invalidateQueries({ queryKey: ['user-profile', user?.id] });
      toast.success('Avatar updated successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  return {
    profile,
    isLoading,
    updateProfile: updateProfileMutation.mutate,
    isUpdating: updateProfileMutation.isPending,
    uploadAvatar,
    isUploading: uploading,
  };
};
